import React, { MouseEvent, TouchEvent } from 'react'
import { Global, css } from '@emotion/react'
import styled from '@emotion/styled'
import { useSelector } from 'react-redux'
import { Link } from 'react-router'
import { Icon } from '@mobi/component-library/Assets/Icon'
import { SvgLazy } from '@mobi/component-library/Assets/Svg/SvgLazy'
import { colors, font, layering, measurements } from '@mobi/component-library/Foundations'
import { Icon as IconLegacy } from '@mobi/component-library/Legacy/Icon'
import { hexColors } from '@mobi/settings'
import {
  bottomSafeAreaInset,
  isNativeAppMajorVersionBelow,
  isReactNativeApp,
} from '@mobi/web-native-comms/web'
import { trackBottomNavigationClicked } from '@core/Areas/Home/analytics'
import { navigateToSkyRacePage } from '@core/Areas/RaceCardSky/helpers/navigateToSkyRacePage'
import { NativeAppVersionWithRemovedTabNav } from '@core/Settings/constants'
import { shouldApplySafeAreaBottomPadding } from '@core/Settings/cssConditions'
import { navigateToLastBetLoopPath } from '@core/State/Navigation/helpers/navigateToLastBetLoopPath'
import { navigateToLastRacesPath } from '@core/State/Navigation/helpers/navigateToLastRacesPath'
import { navigateToLastSportsPath } from '@core/State/Navigation/helpers/navigateToLastSportsPath/navigateToLastSportsPath'
import { useFeature } from '@core/Utils/hooks/useFeature'
import { selectIsRadioBusy, selectShouldDisableTabNavigationRender } from '../../Store/selectors'
import { useActiveTab } from './hooks/useActiveTab'
import type { NavigationTabs } from './types'

export const TabNavigation = () => {
  /** Phasing out native bottom tab navigator, in favour of single implementation. New native
   *  versions will use web tab navigation.
   *
   *  useActiveTab will still send comms to legacy native versions for backward compatibility.
   */
  const shouldUseLegacyTabNavigation =
    isReactNativeApp && isNativeAppMajorVersionBelow(NativeAppVersionWithRemovedTabNav)

  const activeTab = useActiveTab(shouldUseLegacyTabNavigation)
  const shouldDisableTabNavigationRender = useSelector(selectShouldDisableTabNavigationRender)

  if (shouldDisableTabNavigationRender || shouldUseLegacyTabNavigation) {
    return null
  }

  return <TabNavigationComponent activeTab={activeTab} />
}

// ===============
// Local Component
// ===============

const enum LocalConstants {
  ActiveTabClassName = 'js-bottom-tab-nav--active',
}

const TabNavigationComponent: React.FC<{ activeTab: NavigationTabs }> = ({ activeTab }) => {
  const bottomNavRef = React.useRef<HTMLDivElement>(null)

  const isBetLoopFeatureActive = useFeature('content-hub-follow-me')
  const isRadioBusy = useSelector(selectIsRadioBusy)

  const handleSkyRacingClick = () => {
    navigateToSkyRacePage()
    trackBottomNavigationClicked('Watch')
  }

  const handleRacesClick = (e: React.MouseEvent) => {
    navigateToLastRacesPath()
    onNavItemClicked(e)
  }

  const handleSportsClick = (e: React.MouseEvent) => {
    navigateToLastSportsPath()
    onNavItemClicked(e)
  }

  const handleBetLoopClick = (e: React.MouseEvent) => {
    navigateToLastBetLoopPath()
    onNavItemClicked(e)
  }

  const isSkyRacingTabActive = activeTab === 'SkyRacing'

  const bodyBottomPadHeight = shouldApplySafeAreaBottomPadding
    ? `calc(${measurements.mobi.bottomTabsHeight} + ${bottomSafeAreaInset})`
    : 0

  return (
    <ContainerStyled ref={bottomNavRef} id='bottomTabNavigator'>
      <Global
        styles={css`
          :root {
            --safe-area-inset-bottom: ${bottomSafeAreaInset};
          }

          body {
            border-bottom: ${bodyBottomPadHeight} solid
              ${bottomNavRef.current
                ? getComputedStyle(bottomNavRef.current).backgroundColor
                : hexColors.white};
          }
        `}
      />

      <Link
        to='/'
        data-tid-bottom-nav-home
        onClick={onNavItemClicked}
        className={activeTab === 'Home' ? LocalConstants.ActiveTabClassName : ''}
      >
        <IconLegacy type='house' size='2.5rem' color='currentColor' />
        <strong>Home</strong>
      </Link>

      <button
        type='button'
        data-tid-bottom-nav-races
        onClick={handleRacesClick}
        className={activeTab === 'Races' ? LocalConstants.ActiveTabClassName : ''}
      >
        <IconLegacy type='racesall' size='2.5rem' color='currentColor' />
        <strong>Races</strong>
      </button>

      {isBetLoopFeatureActive && (
        <button
          type='button'
          onClick={handleBetLoopClick}
          className={activeTab === 'BetLoop' ? LocalConstants.ActiveTabClassName : ''}
        >
          <Icon name='CustomBetLoopLogo' size='2.5rem' color='currentColor' />
          <strong>Bet Loop</strong>
        </button>
      )}

      <button
        type='button'
        data-tid-bottom-nav-sports
        onClick={handleSportsClick}
        className={activeTab === 'Sports' ? LocalConstants.ActiveTabClassName : ''}
      >
        <IconLegacy type='FBL' size='2.5rem' color='currentColor' />
        <strong>Sports</strong>
      </button>

      <button
        type='button'
        disabled={isRadioBusy}
        data-tid-bottom-nav-watch
        onClick={handleSkyRacingClick}
      >
        <SvgLazy
          name='SkyRacing'
          color={isSkyRacingTabActive ? '' : hexColors.grey}
          height='2.5rem'
        />
        <strong className={isSkyRacingTabActive ? LocalConstants.ActiveTabClassName : ''}>
          Racing
        </strong>
      </button>
    </ContainerStyled>
  )
}

// =============
// Local Helpers
// =============

function onNavItemClicked(e: MouseEvent | TouchEvent) {
  const text = (e.currentTarget?.textContent ?? '').trim()
  if (text) trackBottomNavigationClicked(text)
}

// ======
// Styles
// ======

const ContainerStyled = styled.div({
  position: 'fixed',
  zIndex: layering.bottomTabNavigation,
  bottom: 0,
  left: 0,
  right: 0,
  display: 'flex',
  alignItems: 'stretch',
  margin: '0 auto',
  height: measurements.mobi.bottomTabsHeight,
  minHeight: measurements.mobi.bottomTabsHeight,
  width: '100%',
  minWidth: measurements.mobi.minWidth,
  maxWidth: measurements.mobi.maxWidth,
  paddingBottom: shouldApplySafeAreaBottomPadding ? bottomSafeAreaInset : 0,
  backgroundColor: '#FEFEFE',
  color: hexColors.grey,
  borderTop: `1px solid ${hexColors.gainsboro}`,
  transform: 'translate3d(0,0,0)',

  strong: {
    paddingTop: '0.2rem',
    fontFamily: font.family.primary,
    fontSize: font.size.xs.fontSize,
    letterSpacing: font.size.xs.letterSpacing,
    lineHeight: font.size.xs.lineHeight,
    fontWeight: font.weight.regular,
  },

  'button, a': {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textDecoration: 'none',
    background: 'none',
    color: 'inherit',
    border: 0,
    margin: 0,
    padding: 0,
    WebkitTapHighlightColor: 'transparent',

    '&:active': {
      background: hexColors.veryLightGrey,
    },
  },

  [`.${LocalConstants.ActiveTabClassName}`]: {
    color: colors.lavender[600],
  },
})
